<template>
  <v-card flat>
    <v-card-text>
      <v-container>
        <!-- Скелетон для информации об устройстве -->
        <v-row v-if="loadingDetails && !deviceData">
          <v-col cols="12">
            <h3 class="mb-3">
              <v-icon left>mdi-information-outline</v-icon>
              {{ $t('wialon.device_info') }}
            </h3>
            <v-row>
              <v-col v-for="i in 8" :key="'skeleton-info-' + i" cols="6" sm="4" md="3" lg="2">
                <v-skeleton-loader type="list-item-two-line" class="mx-0"></v-skeleton-loader>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Информация об устройстве -->
        <v-row v-if="deviceData">
          <v-col cols="12">
            <h3 class="mb-3">
              <v-icon left>mdi-information-outline</v-icon>
              {{ $t('wialon.device_info') }}
              <v-progress-circular v-if="loadingDetails" indeterminate size="16" width="2" class="ml-2"></v-progress-circular>
            </h3>
            <v-row>
              <v-col 
                v-for="item in deviceInfoItems" 
                :key="item.label" 
                cols="6" 
                sm="4" 
                md="3" 
                lg="2"
              >
                <v-list-item dense class="px-0">
                  <v-list-item-icon class="mr-2"><v-icon small :color="item.color || ''">{{ item.icon }}</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-subtitle class="text-caption">{{ item.label }}</v-list-item-subtitle>
                    <v-list-item-title :class="item.textColor ? item.textColor + '--text' : ''">{{ item.value }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
            </v-row>
            
            <!-- Статус устройства -->
            <v-row class="mt-2">
              <v-col cols="12">
                <v-chip :color="statusColor" dark small class="mr-2">
                  <v-icon left small>mdi-wifi</v-icon>
                  {{ $t('wialon.status_' + (deviceData.status || 'unknown')) }}
                </v-chip>
              </v-col>
            </v-row>
            
            <!-- Debug: Raw JSON -->
            <v-row class="mt-3">
              <v-col cols="12">
                <v-expansion-panels flat>
                  <v-expansion-panel>
                    <v-expansion-panel-header class="grey--text">
                      <v-icon small class="mr-2">mdi-code-json</v-icon>
                      Raw JSON
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <pre 
                        :class="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-4'"
                        style="max-height: 400px; overflow: auto; padding: 10px; font-size: 11px; border-radius: 4px;"
                      >{{ JSON.stringify(deviceData, null, 2) }}</pre>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Геолокация -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">
              {{ $t('wialon.geolocation') }}
              <v-progress-circular v-if="loadingPosition" indeterminate size="16" width="2" class="ml-2"></v-progress-circular>
            </h3>
            <v-row align="center">
              <v-col cols="12" md="8">
                <v-skeleton-loader v-if="loadingPosition && !position" type="text" class="my-2"></v-skeleton-loader>
                
                <div v-else-if="position">
                  <p>
                    <strong>{{ $t('wialon.coordinates') }}:</strong> 
                    {{ position.lat?.toFixed(6) }}, {{ position.lon?.toFixed(6) }}
                    <span v-if="position.ts" class="ml-2 grey--text text--darken-1">
                      ({{ $t('wialon.updated') }}: {{ formatDateTime(position.ts) }})
                    </span>
                  </p>
                  <p v-if="position.speed !== undefined">
                    <strong>{{ $t('wialon.speed') }}:</strong> {{ position.speed }} {{ $t('custom.kmh') }}
                  </p>
                  <p v-if="position.satellites !== undefined">
                    <strong>{{ $t('wialon.satellites') }}:</strong> {{ position.satellites }}
                  </p>
                </div>
                <div v-else>
                  <p class="grey--text">{{ $t('wialon.no_position_data') }}</p>
                </div>
              </v-col>
              <v-col cols="12" md="4" class="text-right">
                <v-btn color="primary" small @click="openMap" :disabled="!position" class="mr-2">
                  <v-icon left small>mdi-map-marker</v-icon>
                  {{ $t('wialon.open_on_map') }}
                </v-btn>
                <v-btn color="secondary" small @click="refreshPosition" :loading="loadingPosition">
                  <v-icon left small>mdi-refresh</v-icon>
                  {{ $t('wialon.refresh') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Управление -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('wialon.controls') }}</h3>
            
            <v-row v-if="controlsLoaded && availableControls.length === 0">
              <v-col cols="12">
                <v-alert type="info" dense outlined>
                  {{ $t('wialon.no_commands_available') }}
                </v-alert>
              </v-col>
            </v-row>
            
            <v-row v-else align="center">
              <v-col cols="12" md="6" lg="5">
                <v-autocomplete
                  v-model="selectedCommand"
                  :items="availableControls"
                  item-text="name"
                  item-value="id"
                  :label="$t('wialon.select_command')"
                  :placeholder="$t('wialon.select_command_placeholder')"
                  outlined
                  dense
                  clearable
                  :disabled="executingCommand"
                  :loading="loadingControls"
                  return-object
                  @click="onControlsFocus"
                >
                  <template v-slot:item="{ item }">
                    <span>{{ item.name }}</span>
                    <span v-if="item.link_type" class="ml-2 grey--text text-caption">({{ item.link_type }})</span>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" md="3" lg="2">
                <v-btn
                  color="primary"
                  :loading="executingCommand"
                  :disabled="!selectedCommand || executingCommand"
                  @click="sendSelectedCommand"
                  block
                  class="mb-6"
                >
                  <v-icon left>mdi-send</v-icon>
                  {{ $t('wialon.send_command') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Пробег -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('wialon.mileage') }}</h3>
            <v-row align="center">
              <v-col cols="12" md="8">
                <p>
                  <strong>{{ $t('wialon.system_mileage') }}:</strong> {{ car.mileage || 0 }} {{ $t('custom.km') }}
                </p>
                <v-alert 
                  v-if="mileageChecked && !wialonMileageAvailable" 
                  type="warning" 
                  dense 
                  outlined 
                  class="mt-2"
                >
                  {{ mileageMessage || $t('wialon.mileage_unavailable') }}
                </v-alert>
                <p v-if="wialonMileage !== null">
                  <strong>{{ $t('wialon.wialon_mileage_value') }}:</strong> {{ wialonMileage }} {{ $t('custom.km') }}
                  <span v-if="mileageDifference !== null" 
                        :class="Math.abs(mileageDifference) > mileageThreshold ? 'red--text' : 'green--text'">
                    ({{ mileageDifference > 0 ? '+' : '' }}{{ mileageDifference }} {{ $t('custom.km') }})
                  </span>
                </p>
              </v-col>
              <v-col cols="12" md="4" class="text-right">
                <v-btn color="info" small @click="fetchMileage" :loading="loadingMileage" class="mr-2">
                  <v-icon left small>mdi-refresh</v-icon>
                  {{ $t('wialon.check_mileage') }}
                </v-btn>
                <v-btn 
                  color="success" 
                  small 
                  @click="syncMileage" 
                  :loading="syncingMileage"
                  :disabled="!wialonMileageAvailable || wialonMileage === null || Math.abs(mileageDifference) > mileageThreshold"
                >
                  <v-icon left small>mdi-sync</v-icon>
                  {{ $t('wialon.sync_mileage') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- История треков -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('wialon.track_history') }}</h3>
            <v-row align="center">
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="trackStartMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="trackStartDate"
                      :label="$t('wialon.start_date')"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="trackStartDate"
                    @input="trackStartMenu = false"
                    :max="today"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="trackEndMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="trackEndDate"
                      :label="$t('wialon.end_date')"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="trackEndDate"
                    @input="trackEndMenu = false"
                    :max="today"
                    :min="trackStartDate"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-btn color="primary" @click="fetchTracks" :loading="loadingTracks" block>
                  <v-icon left>mdi-map-marker-path</v-icon>
                  {{ $t('wialon.show_tracks') }}
                </v-btn>
              </v-col>
            </v-row>
            
            <v-row v-if="trackData">
              <v-col cols="12">
                <v-alert type="info" dense outlined>
                  <strong>{{ $t('wialon.track_mileage') }}:</strong> {{ trackData.mileage }} {{ $t('custom.km') }}
                  <span class="ml-3">
                    <strong>{{ $t('wialon.track_time') }}:</strong> {{ formatDuration(trackData.moving_time) }}
                  </span>
                  <span class="ml-3">
                    <strong>{{ $t('wialon.track_points') }}:</strong> {{ trackData.way?.length || 0 }}
                  </span>
                </v-alert>
                <v-btn 
                  v-if="trackData.way?.length > 0"
                  color="primary" 
                  small 
                  @click="openTrackOnMap"
                  class="mt-2"
                >
                  <v-icon left small>mdi-map</v-icon>
                  {{ $t('wialon.open_track_on_map') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- История событий -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('wialon.event_history') }}</h3>
            <v-row align="center">
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="eventStartMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="eventStartDate"
                      :label="$t('wialon.start_date')"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="eventStartDate"
                    @input="eventStartMenu = false"
                    :max="today"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="eventEndMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="eventEndDate"
                      :label="$t('wialon.end_date')"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="eventEndDate"
                    @input="eventEndMenu = false"
                    :max="today"
                    :min="eventStartDate"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-btn color="primary" @click="fetchEvents" :loading="loadingEvents" block>
                  <v-icon left>mdi-history</v-icon>
                  {{ $t('wialon.show_events') }}
                </v-btn>
              </v-col>
            </v-row>
            
            <v-row v-if="events && events.length > 0">
              <v-col cols="12">
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th>{{ $t('wialon.event_time') }}</th>
                        <th>{{ $t('wialon.event_type') }}</th>
                        <th>{{ $t('wialon.event_text') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(event, index) in events" :key="index">
                        <td>{{ formatDateTime(event.ts) }}</td>
                        <td>{{ event.type }}</td>
                        <td>{{ event.text }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>
            <v-row v-else-if="eventsLoaded">
              <v-col cols="12">
                <v-alert type="info" dense outlined>
                  {{ $t('wialon.no_events') }}
                </v-alert>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

      </v-container>
    </v-card-text>

    <!-- Snackbar для уведомлений -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          {{ $t('custom.close') }}
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Диалог с картой трека -->
    <v-dialog v-model="trackMapDialog" max-width="900" persistent>
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span>{{ $t('wialon.track_on_map') }}</span>
          <v-btn icon @click="trackMapDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <GmapMap
            v-if="trackMapDialog && trackPath.length > 0"
            :center="trackCenter"
            :zoom="trackZoom"
            style="width: 100%; height: 500px;"
            :options="{
              zoomControl: true,
              mapTypeControl: true,
              scaleControl: true,
              streetViewControl: false,
              rotateControl: false,
              fullscreenControl: true
            }"
          >
            <!-- Линия трека -->
            <GmapPolyline
              :path="trackPath"
              :options="{
                strokeColor: '#1976D2',
                strokeOpacity: 0.9,
                strokeWeight: 4
              }"
            />
            <!-- Маркер начала -->
            <GmapMarker
              :position="trackPath[0]"
              :icon="{
                url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png'
              }"
              :title="$t('wialon.track_start')"
            />
            <!-- Маркер конца -->
            <GmapMarker
              :position="trackPath[trackPath.length - 1]"
              :icon="{
                url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png'
              }"
              :title="$t('wialon.track_end')"
            />
          </GmapMap>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="trackMapDialog = false">
            {{ $t('custom.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: "WialonTab",
  props: {
    car: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      // Загрузка
      loadingDetails: false,
      loadingPosition: false,
      loadingControls: false,
      loadingMileage: false,
      loadingTracks: false,
      loadingEvents: false,
      syncingMileage: false,
      executingCommand: false,
      
      // Данные устройства
      deviceData: null,
      position: null,
      
      // Управление
      availableControls: [],
      controlsLoaded: false,
      selectedCommand: null,
      
      // Пробег
      wialonMileage: null,
      wialonMileageAvailable: false,
      mileageChecked: false,
      mileageMessage: null,
      mileageThreshold: 500,
      
      // Карта трека
      trackMapDialog: false,
      trackPath: [],
      trackCenter: { lat: 55.75, lng: 37.62 },
      trackZoom: 12,
      
      // Треки
      trackStartDate: null,
      trackEndDate: null,
      trackStartMenu: false,
      trackEndMenu: false,
      trackData: null,
      
      // События
      eventStartDate: null,
      eventEndDate: null,
      eventStartMenu: false,
      eventEndMenu: false,
      events: [],
      eventsLoaded: false,
      
      // Snackbar
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success"
    };
  },
  computed: {
    today() {
      return new Date().toISOString().substr(0, 10);
    },
    mileageDifference() {
      if (this.wialonMileage === null) return null;
      return Math.round(this.wialonMileage - (this.car.mileage || 0));
    },
    statusColor() {
      const status = this.deviceData?.status;
      if (status === 'online') return 'success';
      if (status === 'idle') return 'warning';
      return 'grey';
    },
    deviceInfoItems() {
      if (!this.deviceData) return [];
      
      const items = [];
      
      if (this.deviceData.name) {
        items.push({
          icon: 'mdi-tag',
          label: this.$t('wialon.device_name'),
          value: this.deviceData.name
        });
      }
      
      if (this.deviceData.imei) {
        items.push({
          icon: 'mdi-identifier',
          label: 'IMEI',
          value: this.deviceData.imei
        });
      }
      
      if (this.deviceData.phone) {
        items.push({
          icon: 'mdi-phone',
          label: this.$t('wialon.phone'),
          value: this.deviceData.phone
        });
      }
      
      if (this.deviceData.hw_type) {
        items.push({
          icon: 'mdi-chip',
          label: this.$t('wialon.device_type'),
          value: this.deviceData.hw_type
        });
      }
      
      if (this.deviceData.counters?.mileage !== undefined) {
        items.push({
          icon: 'mdi-counter',
          label: this.$t('wialon.counter_mileage'),
          value: `${Math.round(this.deviceData.counters.mileage)} ${this.$t('custom.km')}`
        });
      }
      
      if (this.deviceData.counters?.engine_hours !== undefined) {
        items.push({
          icon: 'mdi-clock-outline',
          label: this.$t('wialon.engine_hours'),
          value: `${this.deviceData.counters.engine_hours?.toFixed(1)} ${this.$t('custom.hours')}`
        });
      }
      
      if (this.deviceData.last_message?.speed !== undefined) {
        items.push({
          icon: 'mdi-speedometer',
          label: this.$t('wialon.speed'),
          value: `${this.deviceData.last_message.speed} ${this.$t('custom.kmh')}`
        });
      }
      
      return items;
    }
  },
  mounted() {
    this.initializeDates();
    this.fetchDetails();
    this.fetchPosition();
  },
  methods: {
    getAuthHeaders() {
      return {
        headers: {
          Authorization: this.$store.getters.getAuthToken,
        },
      };
    },
    
    initializeDates() {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      this.trackStartDate = yesterday.toISOString().substr(0, 10);
      this.trackEndDate = today.toISOString().substr(0, 10);
      this.eventStartDate = yesterday.toISOString().substr(0, 10);
      this.eventEndDate = today.toISOString().substr(0, 10);
    },
    
    async fetchDetails() {
      this.loadingDetails = true;
      try {
        const response = await this.axios.get(`/api/v1/wialon/${this.car.id}/details`, this.getAuthHeaders());
        this.deviceData = response.data.data || response.data;
        
        // Обновляем позицию из данных устройства
        if (this.deviceData.position) {
          this.position = this.deviceData.position;
        }
        
        // Обновляем пробег из счетчиков
        if (this.deviceData.counters?.mileage !== undefined) {
          this.wialonMileage = Math.round(this.deviceData.counters.mileage);
          this.wialonMileageAvailable = true;
          this.mileageChecked = true;
        }
      } catch (error) {
        console.error('Failed to fetch Wialon details:', error);
        this.showSnackbar(this.$t('wialon.error_fetch_details'), 'error');
      } finally {
        this.loadingDetails = false;
      }
    },
    
    async fetchPosition() {
      this.loadingPosition = true;
      try {
        const response = await this.axios.get(`/api/v1/wialon/${this.car.id}/position`, this.getAuthHeaders());
        this.position = response.data;
      } catch (error) {
        console.error('Failed to fetch position:', error);
      } finally {
        this.loadingPosition = false;
      }
    },
    
    refreshPosition() {
      this.fetchPosition();
    },
    
    async onControlsFocus() {
      if (this.controlsLoaded) return;
      
      this.loadingControls = true;
      try {
        const response = await this.axios.get(`/api/v1/wialon/${this.car.id}/controls_library`, this.getAuthHeaders());
        this.availableControls = response.data.controls || [];
        this.controlsLoaded = true;
      } catch (error) {
        console.error('Failed to fetch controls:', error);
        this.showSnackbar(this.$t('wialon.error_fetch_controls'), 'error');
      } finally {
        this.loadingControls = false;
      }
    },
    
    async sendSelectedCommand() {
      if (!this.selectedCommand) return;
      
      this.executingCommand = true;
      try {
        const response = await this.axios.post(`/api/v1/wialon/${this.car.id}/command`, {
          name: this.selectedCommand.name,
          // Пустой тип связи — Wialon выбирает его сам (unit/exec_cmd); подставлять tcp нельзя.
          link_type: this.selectedCommand.link_type || ''
        }, this.getAuthHeaders());
        
        if (response.data.success) {
          this.showSnackbar(this.$t('wialon.command_sent'), 'success');
          // Обновляем данные устройства через 2 секунды
          setTimeout(() => this.fetchDetails(), 2000);
        } else {
          this.showSnackbar(response.data.error || this.$t('wialon.command_failed'), 'error');
        }
      } catch (error) {
        console.error('Failed to send command:', error);
        this.showSnackbar(error.response?.data?.error || this.$t('wialon.command_failed'), 'error');
      } finally {
        this.executingCommand = false;
        this.selectedCommand = null;
      }
    },
    
    async fetchMileage() {
      this.loadingMileage = true;
      try {
        const response = await this.axios.get(`/api/v1/wialon/${this.car.id}/mileage`, this.getAuthHeaders());
        this.wialonMileage = response.data.wialon_mileage;
        this.wialonMileageAvailable = response.data.wialon_available;
        this.mileageMessage = response.data.message;
        this.mileageChecked = true;
        
        if (response.data.wialon_mileage_threshold) {
          this.mileageThreshold = response.data.wialon_mileage_threshold;
        }
      } catch (error) {
        console.error('Failed to fetch mileage:', error);
        this.showSnackbar(this.$t('wialon.error_fetch_mileage'), 'error');
      } finally {
        this.loadingMileage = false;
      }
    },
    
    async syncMileage() {
      this.syncingMileage = true;
      try {
        const response = await this.axios.post(`/api/v1/wialon/${this.car.id}/mileage/sync`, {}, this.getAuthHeaders());
        
        if (response.data.success) {
          this.showSnackbar(
            `${this.$t('wialon.mileage_synced')}: ${response.data.old_mileage} → ${response.data.new_mileage} ${this.$t('custom.km')}`,
            'success'
          );
          // Обновляем данные автомобиля
          this.$emit('car-updated');
        } else {
          this.showSnackbar(response.data.error || this.$t('wialon.sync_failed'), 'error');
        }
      } catch (error) {
        console.error('Failed to sync mileage:', error);
        this.showSnackbar(error.response?.data?.error || this.$t('wialon.sync_failed'), 'error');
      } finally {
        this.syncingMileage = false;
      }
    },
    
    async fetchTracks() {
      this.loadingTracks = true;
      try {
        const beginTs = new Date(this.trackStartDate).getTime() / 1000;
        const endTs = new Date(this.trackEndDate + 'T23:59:59').getTime() / 1000;
        
        const response = await this.axios.get(`/api/v1/wialon/${this.car.id}/messages`, {
          params: { begin: Math.floor(beginTs), end: Math.floor(endTs) },
          ...this.getAuthHeaders()
        });
        
        if (response.data.success) {
          this.trackData = response.data;
        } else {
          this.showSnackbar(response.data.error || this.$t('wialon.error_fetch_tracks'), 'error');
        }
      } catch (error) {
        console.error('Failed to fetch tracks:', error);
        this.showSnackbar(this.$t('wialon.error_fetch_tracks'), 'error');
      } finally {
        this.loadingTracks = false;
      }
    },
    
    async fetchEvents() {
      this.loadingEvents = true;
      try {
        const periodStart = new Date(this.eventStartDate).getTime() / 1000;
        const periodEnd = new Date(this.eventEndDate + 'T23:59:59').getTime() / 1000;
        
        const response = await this.axios.get(`/api/v1/wialon/${this.car.id}/events`, {
          params: { period_start: Math.floor(periodStart), period_end: Math.floor(periodEnd) },
          ...this.getAuthHeaders()
        });
        
        this.events = response.data.events || [];
        this.eventsLoaded = true;
      } catch (error) {
        console.error('Failed to fetch events:', error);
        this.showSnackbar(this.$t('wialon.error_fetch_events'), 'error');
      } finally {
        this.loadingEvents = false;
      }
    },
    
    openMap() {
      if (!this.position) return;
      
      const { lat, lon } = this.position;
      const locale = this.$i18n.locale;
      
      // Используем Яндекс.Карты для ru, Google Maps для остальных
      const url = locale === 'ru'
        ? `https://yandex.ru/maps/?pt=${lon},${lat}&z=15&l=map`
        : `https://www.google.com/maps?q=${lat},${lon}`;
      
      window.open(url, '_blank');
    },
    
    openTrackOnMap() {
      if (!this.trackData?.way?.length) return;
      
      const way = this.trackData.way;
      
      // Преобразуем точки в формат для Google Maps
      this.trackPath = way.map(p => ({ lat: p.lat, lng: p.lon }));
      
      // Вычисляем центр трека
      const lats = way.map(p => p.lat);
      const lons = way.map(p => p.lon);
      const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;
      this.trackCenter = { lat: centerLat, lng: centerLon };
      
      // Вычисляем масштаб на основе размера трека
      const latDiff = Math.max(...lats) - Math.min(...lats);
      const lonDiff = Math.max(...lons) - Math.min(...lons);
      const maxDiff = Math.max(latDiff, lonDiff);
      
      if (maxDiff > 1) this.trackZoom = 8;
      else if (maxDiff > 0.5) this.trackZoom = 9;
      else if (maxDiff > 0.2) this.trackZoom = 10;
      else if (maxDiff > 0.1) this.trackZoom = 11;
      else if (maxDiff > 0.05) this.trackZoom = 12;
      else if (maxDiff > 0.02) this.trackZoom = 13;
      else this.trackZoom = 14;
      
      // Открываем диалог с картой
      this.trackMapDialog = true;
    },
    
    formatDateTime(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp * 1000);
      return date.toLocaleString(this.$i18n.locale);
    },
    
    formatDuration(seconds) {
      if (!seconds) return '0';
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      
      if (hours > 0) {
        return `${hours}${this.$t('custom.h')} ${minutes}${this.$t('custom.m')}`;
      }
      return `${minutes}${this.$t('custom.m')}`;
    },
    
    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    }
  }
};
</script>

<style scoped>
.v-list-item {
  min-height: auto;
}
</style>
